import React, { useCallback } from 'react';
import { FormGroup, TextArea, FormSelect } from '~/components/form';
import { AiFillHome } from 'react-icons/ai';
import { useForm } from '~/hooks';
import { useSectionLoader, useAutoSave } from '../../hooks';
import { FileDrop } from '~/components';
import { requiredALTA } from '~/api';
import sectionTypes from '../sectionTypes';
import { DocumentType } from '~/api/document';

/**
 * @typedef AdditionalInformationValues
 * @property {number}   sectionId
 * @property {string}   mobileHome
 * @property {string}   includeMobileHomeCoverage
 * @property {string}   constructionInProgress
 * @property {string}   mechanicsLienAgent
 * @property {string}   updateFromPrevious
 * @property {string}   book
 * @property {string}   page
 * @property {string}   lienAgentName
 * @property {string}   additionalInformation
 * @property {string}   additionalInformations
 * @property {string}   additionalInformationss
 * @property {[object]} initialDefaultData
 * @typedef FormState
 * @property {AdditionalInformationValues} values The current form values
 */

/** @type {AdditionalInformationValues} */
const defaultState = {
    sectionId: 0,
    orderNumber: null,
    mobileHome: 'No',
    includeMobileHomeCoverage: '',
    constructionInProgress: '',
    mechanicsLienAgent: '',
    updateFromPrevious: '',
    book: '',
    page: '',
    lienAgentName: '',
    requiredALTAPrimary: null,
    
    propertyOccupiedBy: {label: '', value: ''},
    propertyOccupiedByOptions: [
        {label: 'Owner', value: 'Owner'},
        {label: 'Tenant', value: 'Tenant'},
        {label: 'Unimproved', value: 'Unimproved'},
        {label: 'Unknown', value: 'Unknown'}
    ],
    additionalInformation: '',
    additionalInformatios: '',
    additionalInformationss: '',
    initialDefaultData: {
        mobileHome: 'No',
        includeMobileHomeCoverage: 'No',
        constructionInProgress: 'No',
        updateFromPrevious: 'No'
    },
    documents: [],
    priorPolicy: []
};

const AdditionalInformation = ({ titleOpinionId, readOnly }) => {
    const { onLoad: loadSection } = useSectionLoader(sectionTypes.ADDITIONAL_INFO, titleOpinionId);

    const onLoad = useCallback(async (values) => {
        const section = await loadSection(values);
        const requiredALTAOptions = (await requiredALTA.getRequiredALTAs()).map(f => ({
            label: f.label, value: f.value
        }));
        

        let priorPolicy = [];
        let documents = [];

        if (Array.isArray(section.documents)) {
            priorPolicy = section.documents.filter(d => d.type == DocumentType.PTO_PriorPolicy);
            documents = section.documents.filter(d => d.type != DocumentType.PTO_PriorPolicy);
        }

        return {
            ...section,
            documents,
            priorPolicy,
            requiredALTAOptions
        };

    }, [titleOpinionId]);

    const {
        /** @type {FormState} */
        state,
        fieldActions
    } = useForm({
        /** @type {TaxesAndSpecialAssesmentsValues} */
        initialState: { ...defaultState },
        onLoad,
        debug: true
    });

    const { saveField, saveDocument, deleteDocument } = useAutoSave(sectionTypes.ADDITIONAL_INFO, titleOpinionId, state.values.initialDefaultData, state.values.sectionId);
   
    const onBlur = (value, path, validator) => {
        saveField(value, path, state.values.initialDefaultData, state.values.sectionId);
        return fieldActions.onBlur(value, path, validator);
    };
    
    const onChangeHandler = async (event) => {
        await saveField(event.target.value, event.target.name, state.values.initialDefaultData, state.values.sectionId);
        return fieldActions.onChange(event.target.value, event.target.name);
    };

    return (
        <div className="ml-5 mr-5">
            <div className="mb-5">
                <div>
                    <div className="z-10 flex items-center h-full text-xl font-semibold text-blue-60">
                        <div className="h-11 w-11 rounded-md flex items-center justify-center bg-blue-30"><AiFillHome size="45%"/></div>
                        <div className="ml-2"> Additional Information </div>
                    </div> 
                    
                </div>
            </div>
            <div className="text-sm mb-2">Please note any information changes will overwrite any previously entered. Changes are saved automatically.</div>            
            <div className="col-span-12 border-t-2 border-blueGray-30 mt-4 mb-4"/>
            <div className="col-span-8 grid grid-cols-12 grid-flow-row gap-x-8 gap-y-8 text-sm bold mb-4">
                
                <div className="col-span-6 flex flex-direction:row gap-x-5 gap-y-4">
                    <p className="font-medium text-neutral-90">
                        Is a mobile or manufactured home located on the property ? 
                    </p>
                    <input type="radio" value="Yes" name="mobileHome" 
                        checked={state.values.mobileHome === 'Yes'}
                        disabled={readOnly}
                        //className="disabled:bg-neutral-70"
                        onChange={onChangeHandler}
                        
                    /> Yes
                    <input type="radio" value="No" name="mobileHome"
                        checked={state.values.mobileHome === 'No'} 
                        disabled={readOnly}
                        //className="disabled:bg-neutral-70"
                        onChange={onChangeHandler}
                    /> No
                </div>
                {state.values.mobileHome === 'Yes' ?
                    <div className="col-span-6 flex flex-direction:row gap-x-5 gap-y-4">
                        <p className="font-medium text-neutral-90">
                            Do you request that the policy include coverage for any mobile or manufactured home as part of the insured land ?
                        </p>
                        <input type="radio" value="Yes" name="includeMobileHomeCoverage" 
                            checked={state.values.includeMobileHomeCoverage === 'Yes'}
                            disabled={readOnly}
                            //className="disabled:bg-neutral-70"
                            onChange={onChangeHandler}
                            
                        /> Yes
                        <input type="radio" value="No" name="includeMobileHomeCoverage"
                            checked={state.values.includeMobileHomeCoverage === 'No'} 
                            disabled={readOnly}
                            //className="disabled:bg-neutral-70"
                            onChange={onChangeHandler}
                        /> No
                    </div>
                    :
                    <div className="col-span-6 flex flex-direction:row gap-x-5 gap-y-4"></div>
                }
                
                {
                    state.values.mobileHome === 'Yes' && state.values.includeMobileHomeCoverage === 'Yes' ?
                        <div className="col-span-12 grid grid-cols-12 grid-flow-row gap-x-5 gap-y-4">
                            <div className="col-span-4"> Declaration of intent to affix or the MVR46G recording information </div>
                            <FormGroup
                                label="Book"
                                required
                                readOnly={readOnly}
                                groupClassName="col-span-4"
                                path='book'                 
                                value={state.values.book}
                                error={state.errors.book}
                                {...fieldActions}
                                onBlur={onBlur}
                            />
                            <FormGroup
                                label="Page"
                                required
                                readOnly={readOnly}
                                groupClassName="col-span-4"
                                path='page'                 
                                value={state.values.page}
                                error={state.errors.page}
                                {...fieldActions}
                                onBlur={onBlur}
                            />
                        </div>
                        :
                        <div className="col-span-12"/>
                }
                

                <div className="col-span-12 border-t-2 border-blueGray-30 mt-4 mb-4"/>
                <div className="col-span-12 font-bold text-lg"> <h2> Mechanics’ Lien Information </h2> </div>
                <div className="col-span-6 flex flex-direction:row gap-x-5 gap-y-4">
                    <p className="font-medium text-neutral-90">
                    Is construction on the property in progress or immediately contemplated or has construction been recently completed ? 
                    </p>
                    <input type="radio" value="Yes" name="constructionInProgress" 
                        checked={state.values.constructionInProgress === 'Yes'}
                        disabled={readOnly}
                        //className="disabled:bg-neutral-70"
                        onChange={onChangeHandler}
                        
                    /> Yes
                    <input type="radio" value="No" name="constructionInProgress"
                        checked={state.values.constructionInProgress === 'No'} 
                        disabled={readOnly}
                        //className="disabled:bg-neutral-70"
                        onChange={onChangeHandler}
                    /> No
                    <input type="radio" value="Unknown" name="constructionInProgress"
                        checked={state.values.constructionInProgress === 'Unknown'} 
                        disabled={readOnly}
                        //className="disabled:bg-neutral-70"
                        onChange={onChangeHandler}
                    /> Unknown
                </div>
                {
                    state.values.constructionInProgress === 'Yes' ?
                        <div className="col-span-6 flex flex-direction:row gap-x-5 gap-y-4">
                            <p className="font-medium text-neutral-90">
                            Has a Mechanics’ Lien Agent been appointed ? 
                            </p>
                            <input type="radio" value="Yes" name="mechanicsLienAgent" 
                                checked={state.values.mechanicsLienAgent === 'Yes'}
                                disabled={readOnly}
                                //className="disabled:bg-neutral-70"
                                onChange={onChangeHandler}
                                
                            /> Yes
                            <input type="radio" value="No" name="mechanicsLienAgent"
                                checked={state.values.mechanicsLienAgent === 'No'} 
                                disabled={readOnly}
                                //className="disabled:bg-neutral-70"
                                onChange={onChangeHandler}
                            /> No
                        </div>
                        :
                        <div className="col-span-6"/>
                }
                
                {
                    state.values.mechanicsLienAgent === 'Yes' && state.values.constructionInProgress === 'Yes' ?
                        <div className="col-span-12 grid grid-cols-12 grid-flow-row gap-x-5 gap-y-4">
                            <div className="col-span-4"> Lien Agent&apos;s Name </div>
                            <FormGroup
                                groupClassName="col-span-8"
                                path='lienAgentName'      
                                readOnly={readOnly}           
                                value={state.values.lienAgentName}
                                error={state.errors.lienAgentName}
                                {...fieldActions}
                                onBlur={onBlur}
                            />
                        </div>
                        :
                        <div className="col-span-12"/>
                }
                
                <div className="col-span-12 border-t-2 border-blueGray-30 mt-4 mb-4"/>                   
                <div className="col-span-12 font-bold text-lg"> <h2> The Search Period Was: </h2> </div>
                <FormGroup
                    label="From"
                    type='date-picker'
                    name='fromSearchPeriod'
                    required
                    readOnly={readOnly}
                    timeInputLabel="Time:"
                    //startDate={'01/01/1975'}
                    dateFormat="MM/dd/yyyy"
                    groupClassName="col-span-4"
                    datePickerClasses="w-full"
                    path='fromSearchPeriod'
                    value={state.values.fromSearchPeriod}
                    error={state.errors.fromSearchPeriod}                    
                    {...fieldActions}
                    onChange={async (fromSearchPeriod) => {
                        saveField(fromSearchPeriod, 'fromSearchPeriod', state.values.initialDefaultData, state.values.sectionId);
                        fieldActions.onChange(fromSearchPeriod, 'fromSearchPeriod');
                        fieldActions.onChange({ label: '...', value: ''}, 'requestedBy');                        
                    }}
                />
                <FormGroup
                    label="Through"
                    type='date-picker'
                    name='throughSearchPeriod'
                    required
                    readOnly={readOnly}
                    //startDate={'01/01/1975'}
                    timeInputLabel="Time:"
                    dateFormat="MM/dd/yyyy"
                    groupClassName="col-span-4"
                    datePickerClasses="w-full"
                    path='throughSearchPeriod'
                    value={state.values.throughSearchPeriod}
                    error={state.errors.throughSearchPeriod}                    
                    {...fieldActions}
                    onChange={async (throughSearchPeriod) => {
                        saveField(throughSearchPeriod, 'throughSearchPeriod', state.values.initialDefaultData, state.values.sectionId);
                        fieldActions.onChange(throughSearchPeriod, 'throughSearchPeriod');
                        fieldActions.onChange({ label: '...', value: ''}, 'requestedBy');                        
                    }}
                />
                <FormGroup
                    label="At"
                    type='date-picker'
                    name='atSearchPeriod'
                    required
                    readOnly={readOnly}
                    timeInputLabel="Time:"
                    dateFormat="h:mm aa"
                    showTimeInput
                    showTimeSelectOnly
                    groupClassName="col-span-4"
                    datePickerClasses="w-full"
                    path='atSearchPeriod'
                    value={state.values.atSearchPeriod}
                    error={state.errors.atSearchPeriod}                    
                    {...fieldActions}
                    onChange={async (atSearchPeriod) => {
                        saveField(atSearchPeriod, 'atSearchPeriod', state.values.initialDefaultData, state.values.sectionId);
                        fieldActions.onChange(atSearchPeriod, 'atSearchPeriod');
                        fieldActions.onChange({ label: '...', value: ''}, 'requestedBy');                        
                    }}
                />
                <div className="col-span-12"></div>
                <FormSelect
                    label="Property Occupied By"
                    type='select'
                    required
                    isSearchable
                    readOnly={readOnly}
                    options={state.values.propertyOccupiedByOptions}
                    isClearable={false}
                    groupClassName="col-span-3 gap-x-5 gap-y-4"
                    path='propertyOccupiedBy'   
                    value={state.values.propertyOccupiedBy}
                    error={state.errors.propertyOccupiedBy}
                    onChange={(propertyOccupiedBy) => {
                        saveField(propertyOccupiedBy, 'propertyOccupiedBy', state.values.initialDefaultData, state.values.sectionId);                                                
                        fieldActions.onChange(propertyOccupiedBy, 'propertyOccupiedBy');
                        return fieldActions.onChange({ label: '...', value: ''}, 'requestedBy');
                    }}       
                />
                
                <span className="col-span-9" />
                {state.values.requiredALTAPrimary.label == 'Other' ?
                    <>
                        <FormSelect
                            label="Required ALTA Endorsements"
                            type='select'
                            options={state.values.requiredALTAOptions}
                            readOnly={readOnly}
                            isClearable={false}
                            isSearchable
                            isMulti = {true}
                            closeOnSelect={false}                            
                            groupClassName="col-span-7 mt-4"
                            path='requiredALTAPrimary'   
                            value={state.values.requiredALTAPrimary}
                            error={state.errors.requiredALTAPrimary}
                            onChange={async (requiredALTA) => {
                                saveField(requiredALTA, 'requiredALTAPrimary', state.values.initialDefaultData, state.values.sectionId);
                                fieldActions.onChange(requiredALTA, 'requiredALTAPrimary');
                                fieldActions.onChange({ label: '...', value: ''}, 'requestedBy');                        
                            }}
                            onBlur={onBlur}             
                        />
                        <FormGroup
                            groupClassName="col-span-2 mt-4 mb-4"
                            path='requiredALTAOtherPrimary' 
                            readOnly={readOnly}                
                            value={state.values.requiredALTAOtherPrimary}
                            error={state.errors.requiredALTAOtherPrimary}
                            {...fieldActions}
                            onBlur={onBlur}
                        />
    
                    </>
    
                    :
                    <FormSelect
                        label="Required ALTA Endorsements"
                        type='select'
                        options={state.values.requiredALTAOptions}                        
                        readOnly={readOnly}
                        isClearable={false}
                        isSearchable
                        closeOnSelect={false}
                        isMulti = {true}
                        groupClassName="col-span-4"
                        path='requiredALTAPrimary'   
                        value={state.values.requiredALTAPrimary}
                        error={state.errors.requiredALTAPrimary}
                        onChange={async (requiredALTA) => {
                            saveField(requiredALTA, 'requiredALTAPrimary', state.values.initialDefaultData, state.values.sectionId);
                            fieldActions.onChange(requiredALTA, 'requiredALTAPrimary');
                            fieldActions.onChange({ label: '...', value: ''}, 'requestedBy');                        
                        }}
                        onBlur={onBlur}             
                    />
                }
                
                <span className="col-span-9" />
                <div className="col-span-6 flex gap-x-5 gap-y-4">
                    <p className="font-medium text-neutral-90">
                    Updating from Previous Title Insurance Policy ? 
                    </p>
                    <input type="radio" value="Yes" name="updateFromPrevious" 
                        checked={state.values.updateFromPrevious === 'Yes'}
                        disabled={readOnly}
                        //className="disabled:bg-neutral-70"
                        onChange={onChangeHandler}
                        
                    /> Yes
                    <input type="radio" value="No" name="updateFromPrevious"
                        checked={state.values.updateFromPrevious === 'No'} 
                        disabled={readOnly}
                        //className="disabled:bg-neutral-70"
                        onChange={onChangeHandler}
                    /> No
                </div>
                {state.values.updateFromPrevious && state.values.updateFromPrevious === 'Yes' ?
                    <FileDrop
                        files={state.values.priorPolicy}
                        className='w-full h-10'
                        groupClassName='col-span-8'
                        readOnly={readOnly}
                        onChange={files => {
                            const documents = files.map(f => {
                                return {
                                    ...f,
                                    type: DocumentType.PTO_PriorPolicy,
                                    uploaded: f.uploaded == undefined ? false : f.uploaded,
                                    isDeleted: f.isDeleted != undefined ? f.isDeleted : false
                                };
                            });
    
                            documents.map(d => {
                                if (!d.uploaded) {
                                    saveDocument(d, d.type, state.values.sectionId);
                                }
                            });
    
                            fieldActions.onChange(documents, 'priorPolicy');
                        }}
                        heading='Attach Copy of Prior Title Policy'
                        onRemove={(_, index) => {
                            const files = [...state.values.priorPolicy];
                            const deleted = files[index];
                            files.splice(index, 1);
                            deleteDocument(deleted.id, state.values.sectionId);
                            fieldActions.onChange(files, 'priorPolicy');
                        }}
                    />
                    :
                    <></>
                }


                <TextArea
                    rows={2}
                    type="textarea"
                    label="Send Owner's Title Policy To"
                    name="additionalInformations"
                    path="additionalInformations"
                    readOnly={readOnly}
                    groupClassName="col-span-8 mt-4"
                    inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                    value={state.values.additionalInformations}
                    error={state.errors.additionalInformations}
                    {...fieldActions}
                    onChange={(additionalInformations) => {
                        saveField(additionalInformations, 'additionalInformations', state.values.initialDefaultData, state.values.sectionId);
                        fieldActions.onChange(additionalInformations, 'additionalInformations');
                    }}
                    onBlur={onBlur}
                />
                
                <TextArea
                    rows={2}
                    type="textarea"
                    label="Send Lender's Title Policy To"
                    name="additionalInformationss"
                    path="additionalInformationss"
                    readOnly={readOnly}
                    placeholder=""
                    groupClassName="col-span-8 mt-4"
                    inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                    value={state.values.additionalInformationss}
                    error={state.errors.additionalInformationss}
                    {...fieldActions}
                    onChange={(additionalInformationss) => {
                        saveField(additionalInformationss, 'additionalInformationss', state.values.initialDefaultData, state.values.sectionId);
                        fieldActions.onChange(additionalInformationss, 'additionalInformationss');
                    }}
                    onBlur={onBlur}
                />

                <TextArea
                    rows={2}
                    type="textarea"
                    label="Additional Information"
                    name="additionalInformation"
                    path="additionalInformation"
                    readOnly={readOnly}
                    placeholder="i.e. Lender’s email, chain of title, or counsel questions"
                    groupClassName="col-span-8 mt-4"
                    inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                    value={state.values.additionalInformation}
                    error={state.errors.additionalInformation}
                    {...fieldActions}
                    onChange={(additionalInformation) => {
                        saveField(additionalInformation, 'additionalInformation', state.values.initialDefaultData, state.values.sectionId);
                        fieldActions.onChange(additionalInformation, 'additionalInformation');
                    }}
                    onBlur={onBlur}
                />


                <FileDrop
                    files={state.values.documents}
                    className='w-full h-16'
                    readOnly={readOnly}
                    groupClassName='col-span-8 mt-4'
                    bracketHeadingClassName="brackdropfile"
                    onChange={files => {
                        const documents = files.map(f => {
                            return {
                                ...f,
                                type: DocumentType.PTO_ATTACHMENT,
                                uploaded: f.uploaded == undefined ? false : f.uploaded,
                                isDeleted: f.isDeleted != undefined ? f.isDeleted : false
                            };
                        });

                        documents.map(d => {
                            if (!d.uploaded) {
                                saveDocument(d, d.type, state.values.sectionId);
                            }
                        });

                        fieldActions.onChange(documents, 'documents');
                    }}
                    multiple
                    heading='Upload Any Additional Documents '
                    bracketHeading='(Lien Waivers, Copy of check, Estate Indemnity, etc.)'
                    onRemove={(_, index) => {
                        const files = [...state.values.documents];
                        const deleted = files[index];
                        files.splice(index, 1);
                        deleteDocument(deleted.id, state.values.sectionId);
                        fieldActions.onChange(files, 'documents');
                    }}
                />
            </div>
        </div>
    );
};

export default AdditionalInformation;